import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  PopoverOnboarding,
  ButtonTertiary,
  Type,
} from '@spotify-internal/encore-web';
import { OverlayDirections } from '@spotify-internal/encore-web/types/src/core/styles';
import { useT } from '@spotify-internal/i18n-core';

const StyledPopoverOnboarding = styled(PopoverOnboarding)<{
  position?: { top?: string; left?: string; right?: string; bottom?: string };
}>`
  position: absolute;
  top: ${({ position }) => position?.top};
  left: ${({ position }) => position?.left};
  right: ${({ position }) => position?.right};
  bottom: ${({ position }) => position?.bottom};
  z-index: 1;
`;

export type AllowedSteps = 'one' | 'two' | 'three';

export interface OnboardingPopoverProps {
  onComplete: () => void;
  onNextClick: () => void;
  onEntry?: (element: HTMLElement | null) => void;
  title: string;
  content: string;
  currentStep: AllowedSteps;
  arrowDirection?: OverlayDirections;
  position?: {
    top?: string | undefined;
    left?: string | undefined;
    right?: string | undefined;
    bottom?: string | undefined;
  };
}

export const OnboardingPopover = ({
  onEntry,
  onComplete,
  onNextClick,
  currentStep,
  content,
  title,
  arrowDirection,
  position,
}: OnboardingPopoverProps) => {
  const t = useT();
  const popoverRef = useRef<HTMLElement>(null);

  const steps = {
    one: t(
      'atlas.walk_through.one_of_three',
      '1 of 3',
      'Indicates this is one of three directions. 1 and 3 should be kept as numbers',
    ),
    two: t(
      'atlas.walk_through.two_of_three',
      '2 of 3',
      'Indicates this is two of three directions. 2 and 3 should be kept as numbers',
    ),
    three: t(
      'atlas.walk_through.three_of_three',
      '3 of 3',
      'Indicates this is three of three directions. 3 and 3 should be kept as numbers',
    ),
  };

  useEffect(() => {
    if (onEntry) {
      onEntry(popoverRef.current);
    }
  }, [onEntry]);

  return (
    <StyledPopoverOnboarding
      arrow={arrowDirection || 'topStart'}
      popoverTitle={title}
      id={`${title}-popover`}
      colorSet="mutedAccent"
      onClose={() => onComplete()}
      onClick={(event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      position={position}
      paginationText={
        <Type as="p" semanticColor="textSubdued" variant="finale">
          {steps[currentStep]}
        </Type>
      }
      footerButton={
        <ButtonTertiary
          condensed
          size="small"
          onClick={() => {
            onNextClick();
          }}
        >
          {currentStep === 'three'
            ? t(
                'atlas.actions.done',
                'Done',
                'Button that indicates user is done',
              )
            : t('atlas.actions.next', 'Next', 'Button text to go to next step')}
        </ButtonTertiary>
      }
      ref={popoverRef}
    >
      {content}
    </StyledPopoverOnboarding>
  );
};
